import {
  additionalIssueQuestionnaire,
  copyQuestionnaire,
  createCondition,
  createQuestion,
  createQuestionnaire,
  createQuestionnairePage,
  deleteCondition,
  deleteQuestion,
  deleteQuestionnaire,
  deleteQuestionnairePage,
  getQuestionnaire,
  getQuestionnaires,
  updateCondition,
  updateQuestion,
  updateQuestionnaire,
  updateQuestionnairePage,
} from '@/api/questionnaire'
import { genMock } from '@/api/receipts'
import { QuestionForm } from '@/composable/questionnaire/useQuestionForm'
import { IQuestionnaire } from '@/types/questionnaire'
import { QuestionnaireState, RootState } from '@/types/store'
import {
  CreateConditionDto,
  CreateQuestionnaireDto,
  CreatePageDto,
  UpdateConditionDto,
  UpdateQuestionnaireDto,
  CreateChoiceDto,
  CreateCrossDto,
} from '../../../api/@types'
import { ActionContext, ActionTree } from 'vuex'

type Context = ActionContext<QuestionnaireState, RootState>

export interface QuestionnaireActions
  extends ActionTree<QuestionnaireState, RootState> {
  createQuestionnaire: (
    ctx: Context,
    dto: CreateQuestionnaireDto,
  ) => Promise<void>
  updateQuestionnaire: (
    ctx: Context,
    payload: { id: number; body: UpdateQuestionnaireDto },
  ) => Promise<void>
  copyQuestionnaire: (ctx: Context, payload: { id: number }) => Promise<void>
  createQuestion: (
    ctx: Context,
    payload: QuestionForm & { pageId: number },
  ) => Promise<void>
  updateQuestion: (
    ctx: Context,
    payload: QuestionForm & { questionId: number },
  ) => Promise<void>
  getCompleteQuestionnaires: (
    ctx: Context,
    payload: { isAutomatic: boolean },
  ) => Promise<void>
}

export const actions: QuestionnaireActions = {
  async getAutomaticQuestionnaires({ commit }, params): Promise<void> {
    commit('setIsQuestionnaireLoading', true)
    const questionnaires = await getQuestionnaires(
      { ...params, isAutomatic: true } || null,
    )
    commit('questionnaireLoaded', questionnaires)
    commit('setIsQuestionnaireLoading', false)
  },
  async getManualQuestionnaires({ commit }, params): Promise<void> {
    commit('setIsQuestionnaireLoading', true)
    const questionnaires = await getQuestionnaires(
      { ...params, isAutomatic: false } || null,
    )
    commit('questionnaireLoaded', questionnaires)
    commit('setIsQuestionnaireLoading', false)
  },
  async createQuestionnaire(
    { commit },
    dto: CreateQuestionnaireDto,
  ): Promise<void> {
    commit('setIsQuestionnaireLoading', true)
    await createQuestionnaire(dto)
    commit('setIsQuestionnaireLoading', false)
  },
  async getQuestionnaire({ commit }, id: number): Promise<void> {
    commit('setIsQuestionnaireLoading', true)
    const questionnaire = await getQuestionnaire(id)
    commit('setQuestionnaire', questionnaire)
    commit('setIsQuestionnaireLoading', false)
  },
  async updateQuestionnaire(
    { commit, dispatch },
    payload: { id: number; body: UpdateQuestionnaireDto },
  ): Promise<void> {
    commit('setIsQuestionnaireLoading', true)
    await updateQuestionnaire(payload.id, payload.body)
    await dispatch('getQuestionnaire', payload.id)
    commit('setIsQuestionnaireLoading', false)
  },
  async deleteQuestionnaire(
    { commit },
    payload: { id: number; body: UpdateQuestionnaireDto },
  ): Promise<void> {
    commit('setIsQuestionnaireLoading', true)
    const questionnaire = await deleteQuestionnaire(payload.id)
    commit('deleteQuestionnaire', questionnaire)
    commit('setIsQuestionnaireLoading', false)
  },
  async additionalIssueQuestionnaire(
    { commit },
    payload: { id: number },
  ): Promise<void> {
    commit('setIsQuestionnaireLoading', true)
    const questionnaire = await additionalIssueQuestionnaire(payload.id)
    commit('setIsQuestionnaireLoading', false)
    commit('deleteQuestionnaire', questionnaire)
  },
  async copyQuestionnaire({ commit }, payload: { id: number }): Promise<void> {
    commit('setIsQuestionnaireLoading', true)
    await copyQuestionnaire(payload.id)
    commit('setIsQuestionnaireLoading', false)
  },
  async genMock(
    _,
    payload: { id: number; receiptId: number },
  ): Promise<string> {
    return await genMock(payload.receiptId, payload.id)
  },
  async createQuestionnairePage({ commit }, body: CreatePageDto) {
    commit('setIsQuestionnaireLoading', true)
    await createQuestionnairePage(body)
    commit('setIsQuestionnaireLoading', false)
  },
  async updateQuestionnairePage(
    { commit, dispatch, state },
    { pageId, ...rest }: { pageId: number },
  ) {
    commit('setIsQuestionnaireLoading', true)
    await updateQuestionnairePage(pageId, rest)
    await dispatch('getQuestionnaire', state.questionnaire.id)
    commit('setIsQuestionnaireLoading', false)
  },
  async deleteQuestionnairePage({ commit, dispatch, state }, pageId: number) {
    commit('setIsQuestionnaireLoading', true)
    await deleteQuestionnairePage(pageId)
    await dispatch('getQuestionnaire', state.questionnaire.id)
    commit('setIsQuestionnaireLoading', false)
  },
  async createQuestion({ commit }, data: QuestionForm & { pageId: number }) {
    commit('setIsQuestionnaireLoading', true)
    const { choices, crosses, enableLastChoice, ...rest } = data

    let createChoices = choices as CreateChoiceDto[]
    if (rest.isRandom && enableLastChoice) {
      createChoices = createChoices.map((choice, i) => {
        choice.isLast = i === createChoices.length - 1
        return choice
      })
    }

    await createQuestion({
      choices: createChoices,
      crosses: crosses as CreateCrossDto[],
      ...rest,
    })
    commit('setIsQuestionnaireLoading', false)
  },
  async updateQuestion(
    { commit },
    data: QuestionForm & { questionId: number },
  ) {
    commit('setIsQuestionnaireLoading', true)
    const { questionId, choices, crosses, enableLastChoice, ...body } = data

    let updateChoices = choices.map(choice => ({ ...choice, questionId }))
    if (body.isRandom && enableLastChoice) {
      updateChoices = updateChoices.map((choice, i) => {
        choice.isLast = i === updateChoices.length - 1
        return choice
      })
    } else {
      updateChoices = updateChoices.map(choice => ({
        ...choice,
        isLast: false,
      }))
    }

    await updateQuestion(questionId, {
      ...body,
      choices: updateChoices,
      crosses: crosses.map(cross => ({ ...cross, questionId })),
      id: questionId,
    })
    commit('setIsQuestionnaireLoading', false)
  },
  async deleteQuestion({ commit, dispatch, state }, questionId: number) {
    commit('setIsQuestionnaireLoading', true)
    await deleteQuestion(questionId)
    await dispatch('getQuestionnaire', state.questionnaire.id)
    commit('setIsQuestionnaireLoading', false)
  },
  async changePageOrder(
    { commit, state },
    { pageId, type }: { pageId: number; type: 'up' | 'down' },
  ) {
    const newQuestionnaire: IQuestionnaire = JSON.parse(
      JSON.stringify(state.questionnaire),
    )

    const oldOrder = newQuestionnaire.pages.find(page => page.id === pageId)
      ?.order
    if (!oldOrder) {
      throw new Error('target page is not found.')
    }

    const newOrder = type === 'up' ? oldOrder - 1 : oldOrder + 1
    newQuestionnaire.pages = newQuestionnaire.pages.map(page => {
      if (page.id === pageId) {
        page.order = newOrder
      } else if (page.order === newOrder && !page.isThankPage) {
        page.order = oldOrder
      }
      return page
    })

    newQuestionnaire.pages.sort((a, b) => a.order - b.order)
    await updateQuestionnairePage(pageId, { order: newOrder })

    commit('setQuestionnaire', newQuestionnaire)
  },
  async changeQuestionOrder(
    { commit, state },
    payload: { pageId: number; questionId: number; type: 'up' | 'down' },
  ) {
    const newQuestionnaire: IQuestionnaire = JSON.parse(
      JSON.stringify(state.questionnaire),
    )

    const targetPage = newQuestionnaire.pages.find(
      page => page.id === payload.pageId,
    )
    if (!targetPage) {
      throw new Error('target page is not found.')
    }

    const oldOrder = targetPage.questions.find(q => q.id === payload.questionId)
      ?.order
    if (!oldOrder) {
      throw new Error('target question is not found.')
    }

    const newOrder = payload.type === 'up' ? oldOrder - 1 : oldOrder + 1
    newQuestionnaire.pages = newQuestionnaire.pages.map(page => {
      if (page.id !== payload.pageId) {
        return page
      }

      page.questions = page.questions.map(q => {
        if (q.id === payload.questionId) {
          q.order = newOrder
        } else if (q.order === newOrder) {
          q.order = oldOrder
        }
        return q
      })
      return page
    })
    newQuestionnaire.pages.map(page =>
      page.questions.sort((a, b) => a.order - b.order),
    )

    await updateQuestion(payload.questionId, {
      order: newOrder,
      id: payload.questionId,
    })
    commit('setQuestionnaire', newQuestionnaire)
  },
  async createCondition(
    { commit, state, dispatch },
    { questionId, body }: { questionId: number; body: CreateConditionDto },
  ) {
    commit('setIsQuestionnaireLoading', true)
    await createCondition(questionId, body)
    await dispatch('getQuestionnaire', state.questionnaire.id)
    commit('setIsQuestionnaireLoading', false)
  },
  async updateCondition(
    { commit, state, dispatch },
    { conditionId, body }: { conditionId: number; body: UpdateConditionDto },
  ) {
    commit('setIsQuestionnaireLoading', true)
    await updateCondition(conditionId, body)
    await dispatch('getQuestionnaire', state.questionnaire.id)
    commit('setIsQuestionnaireLoading', false)
  },
  async deleteCondition({ commit, state, dispatch }, conditionId: number) {
    commit('setIsQuestionnaireLoading', true)
    await deleteCondition(conditionId)
    await dispatch('getQuestionnaire', state.questionnaire.id)
    commit('setIsQuestionnaireLoading', false)
  },
  async getCompleteQuestionnaires({ commit }, { isAutomatic }) {
    commit('setIsQuestionnaireLoading', true)
    const questionnaires = await getQuestionnaires({
      isAutomatic: isAutomatic ?? true,
    })
    commit('setIsQuestionnaireLoading', false)
    commit('setCompletionQuestionnaires', questionnaires)
  },
}
